// frontend/src/components/CookieConsent/CookieConsentBanner.js
//
// Visual language matches SimpleAccessibilitySettings.js (teal/mint gradient
// hero, Heebo headings, tinted icon chips, BrandSwitch) so the site's two
// "settings" panels read as one product. Behavior/consent logic below is
// UNCHANGED from before this redesign - only markup/styling moved.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  Typography,
  Button,
  Switch,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Link,
  Paper,
  GlobalStyles,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  X as CloseIcon,
  ShieldCheck,
  BarChart3,
  Megaphone,
  Lock,
} from 'lucide-react';
import { styled } from '@mui/material/styles';

import {
  getStoredConsent,
  saveConsent,
  isConsentRequired,
  applyConsentPreferences,
  DEFAULT_PREFERENCES,
} from './consentUtils';
import { getConsentTexts } from './consentTexts';
import { colors } from '../../theme/tokens';

// Window event other components dispatch to re-open the preferences dialog
// after consent was already given (see the footer "cookie settings" link).
export const OPEN_SETTINGS_EVENT = 'leadify:open-cookie-settings';

// Unique id on the settings Dialog Paper. Scopes the focus-ring override
// below so it out-specifies the sitewide focus-visible rule (see comment
// near GlobalStyles) without touching that rule anywhere else.
const PANEL_ID = 'cookie-settings-panel';

const FONT_HEAD = '"Heebo", sans-serif';
// "Assistant" was never loaded (see components/legal/LegalDocument.js) - it always
// resolved to Heebo. Dropping the name changes nothing that renders.
const FONT_BODY = '"Heebo", sans-serif';
const ICON_COLOR = colors.primary.accessible;

// One icon per category, matching the tinted-chip language of the
// accessibility settings rows. Necessary uses ShieldCheck (security/trust),
// analytics a bar-chart glyph, marketing a megaphone.
const CATEGORY_ICONS = {
  necessary: ShieldCheck,
  analytics: BarChart3,
  marketing: Megaphone,
};

// First-visit banner palette (owner-approved from a mock). Both actions share
// one soft style family on purpose - the old solid dark-green "accept" was
// judged too loud. Measured text contrast (WCAG): BANNER_ACTION_TEXT is
// 7.53:1 on #fff, 6.42:1 on BANNER_ACCEPT_BG, 5.91:1 on its hover tint;
// BANNER_LINK is 4.91:1 on #fff; BANNER_TEXT is 9.21:1 on #fff.
const BANNER_TEXT = '#3a4a52';
const BANNER_LINK = '#007f72';
const BANNER_ACTION_TEXT = '#0a5f57';
const BANNER_ACTION_BORDER = '#bfe6df';
const BANNER_ACTION_BORDER_HOVER = '#9fd8ce';
const BANNER_ACCEPT_BG = '#d9f2ee';
const BANNER_ACCEPT_BG_HOVER = '#c8ebe5';
const BANNER_ESSENTIAL_BG_HOVER = '#eef8f6';

// Phone/desktop split for the banner. A raw query (not MUI's `sm`, which is
// min-width 600px) so a 600px-wide viewport still gets the phone layout.
const BANNER_DESKTOP = '@media (min-width: 601px)';

/**
 * Shared sx for the banner's two actions: same size, same style family, only
 * the fill differs. MUI's theme sets a 10px 24px padding + 16px font on every
 * Button root, so both are overridden here rather than inherited.
 */
const bannerActionSx = (background, hoverBackground) => ({
  flex: 1,
  minWidth: 0,
  height: 30,
  px: 1.5,
  py: 0,
  fontFamily: FONT_BODY,
  fontSize: '12.5px',
  fontWeight: 600,
  lineHeight: 1,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  borderRadius: '999px',
  color: BANNER_ACTION_TEXT,
  background,
  border: `1px solid ${BANNER_ACTION_BORDER}`,
  boxShadow: 'none',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  '&:hover, &.Mui-focusVisible': {
    background: hoverBackground,
    borderColor: BANNER_ACTION_BORDER_HOVER,
    boxShadow: 'none',
  },
  [BANNER_DESKTOP]: {
    flex: '0 0 auto',
    minWidth: 116,
    height: 34,
    fontSize: '13px',
  },
});

/**
 * Custom-styled Switch matching the brand, with a deliberate RTL fix.
 *
 * MUI's <Switch> hardcodes a *physical* `left: 0` resting position and
 * `transform: translateX(20px)` on the checked state - there is no logical
 * inset-inline-start/end involved, and this project does not load
 * stylis-plugin-rtl. Left untouched, every switch here would rest on the
 * physical LEFT and slide further RIGHT when checked even though this whole
 * dialog is dir="rtl" - the opposite of how a native Hebrew switch behaves.
 * We flip the resting anchor (`right` instead of `left`) and negate the
 * checked transform so the motion mirrors correctly. Copied verbatim from
 * SimpleAccessibilitySettings.js's BrandSwitch to keep both panels' toggles
 * behaving identically.
 */
const BrandSwitch = styled(Switch)(({ theme }) => ({
  width: 46,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    top: 3,
    right: 3,
    left: 'auto',
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(-20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: colors.primary.accessible,
        opacity: 1,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      outline: `2px solid ${colors.primary.accessible}`,
      outlineOffset: 2,
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 13,
    backgroundColor: colors.neutral[300],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], { duration: 200 }),
  },
}));

/**
 * Hebrew is the product default, so the test is for the explicit opt-out
 * (`localStorage.language === 'english'`), never for `=== 'hebrew'` - an absent
 * key means "never toggled". Same rule as PwaAwarenessSnackbar/AuthLayout.
 *
 * Wrapped because this read happens in the RENDER BODY of a component mounted
 * above <Routes> on every page: a blocked/throwing localStorage (Safari private
 * mode) would otherwise take the whole React tree down, not just this banner.
 */
const isHebrewPreferred = () => {
  try {
    return typeof window !== 'undefined'
      ? localStorage.getItem('language') !== 'english'
      : true;
  } catch {
    return true;
  }
};

const RowIcon = ({ Icon }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 40,
      height: 40,
      borderRadius: '12px',
      flexShrink: 0,
      background: colors.primary.alpha08,
      border: `1px solid ${colors.primary.alpha18}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icon size={19} color={ICON_COLOR} strokeWidth={1.8} />
  </Box>
);

const CookieConsentBanner = ({ isHebrew: isHebrewProp }) => {
  // Hebrew is the product default; allow explicit opt-out via prop or
  // localStorage('language' === 'english') to mirror the rest of the app.
  const isHebrew =
    typeof isHebrewProp === 'boolean' ? isHebrewProp : isHebrewPreferred();
  const dir = isHebrew ? 'rtl' : 'ltr';

  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const firstFocusRef = useRef(null);

  // isHebrew is stable for the session; memoize so toggling a switch doesn't
  // rebuild the whole nested text object on every re-render.
  const texts = useMemo(() => getConsentTexts(isHebrew), [isHebrew]);

  // Allow any part of the app (e.g. the footer "cookie settings" link) to
  // re-open the preferences dialog after consent was already given — fulfils
  // the "change your choice at any time" promise and the GDPR right to
  // withdraw consent. Decoupled via a window event so there is no shared store.
  useEffect(() => {
    const openSettings = () => setShowSettings(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    // Check if consent is required on mount
    if (isConsentRequired()) {
      setShowBanner(true);
      // Initialize with default preferences
      setPreferences({ ...DEFAULT_PREFERENCES });
    } else {
      // Load existing preferences
      const stored = getStoredConsent();
      if (stored) {
        setPreferences(stored.preferences);
        // Apply existing preferences
        applyConsentPreferences(stored.preferences);
      }
    }
  }, []);

  // Focus management when the settings dialog opens (close button receives
  // initial focus) — same pattern as SimpleAccessibilitySettings.
  useEffect(() => {
    if (showSettings && firstFocusRef.current) {
      setTimeout(() => {
        firstFocusRef.current?.focus();
      }, 100);
    }
  }, [showSettings]);

  // Persist a final preference set, sync analytics consent, and close the UI.
  // applyConsentPreferences already syncs window.analytics consent, so we do
  // not call updateConsent again here (single source of truth in consentUtils).
  const commitPreferences = (finalPreferences) => {
    saveConsent(finalPreferences);
    applyConsentPreferences(finalPreferences);

    setPreferences(finalPreferences);
    setShowBanner(false);
    setShowSettings(false);
    setShowSuccessMessage(true);
  };

  const handleAcceptAll = () =>
    commitPreferences({ necessary: true, analytics: true, marketing: true });

  const handleRejectAll = () =>
    commitPreferences({ necessary: true, analytics: false, marketing: false });

  // Necessary is force-true regardless of UI state.
  const handleSavePreferences = () =>
    commitPreferences({ ...preferences, necessary: true });

  const handleTogglePreference = (category) => {
    // Necessary cookies are mandatory and never toggleable.
    if (category === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleOpenSettings = () => setShowSettings(true);

  // ---------------------------------------------------------------------------
  // Minimal banner — first-visit notice.
  // Design intent: a small floating glass card that respects the reading flow
  // and covers as little of the page as possible. Phone: inset 10px from the
  // sides and bottom, text (with the "Settings" control inline, as a link)
  // above a row of two equal actions. Desktop: centred at the bottom, one row
  // - text, then the two actions. RTL-native via `dir`. No cookie wall: the
  // page behind stays fully usable, and nothing takes focus on mount.
  //
  // Rendered as a plain function (not <Component/>) so it shares the parent's
  // render pass — avoids creating a new component identity on every state
  // change, which would otherwise remount the subtree and drop focus.
  // ---------------------------------------------------------------------------
  const renderBanner = () => (
    <Paper
      dir={dir}
      // Removed from every prerendered snapshot by scripts/prerender.js
      // (backlog #158). The prerender browser has empty localStorage, so this
      // banner always mounts there and used to be serialized into build/**/
      // index.html - flashing at first paint for every visitor, consented or
      // not. The script hard-fails if this marker stops appearing pre-strip.
      data-prerender-strip="cookie-banner"
      elevation={0}
      role="dialog"
      aria-modal="false"
      aria-label={texts.title}
      sx={{
        position: 'fixed',
        zIndex: 9999,
        // Phone: a card inset 10px from each edge, clear of the notch/home
        // indicator (env() resolves to 0 where there is no safe area).
        left: 'calc(10px + env(safe-area-inset-left, 0px))',
        right: 'calc(10px + env(safe-area-inset-right, 0px))',
        bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
        width: 'auto',
        borderRadius: '14px',
        padding: '10px 12px',
        // Desktop: centre via left/right 0 + auto margins (NOT left:50% +
        // translateX) so the entrance animation's transform: translateY(...)
        // can't clobber the horizontal centering.
        [BANNER_DESKTOP]: {
          left: 0,
          right: 0,
          bottom: 20,
          mx: 'auto',
          // 720 keeps the sentence and the inline "Settings" link on ONE
          // line beside the two actions at 1280 and up (measured in Chrome).
          width: 'min(720px, calc(100% - 48px))',
          borderRadius: '16px',
          // Logical, so the extra breathing room sits on the text's side in
          // both directions.
          paddingBlock: '10px',
          paddingInlineStart: '16px',
          paddingInlineEnd: '12px',
        },
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(15, 60, 70, 0.10)',
        boxShadow: '0 8px 28px rgba(15, 40, 50, 0.14)',
        // The consent banner MUST always be visible, so its base state is
        // fully opaque — the entrance animation only *enhances* the appearance.
        // `animation-fill-mode: both` applies the `from` (opacity 0) during the
        // 120ms delay to avoid a flash, then the `to` state after. If the
        // animation never runs at all (prefers-reduced-motion, or an extension/
        // policy forcing `animation: none`), the element falls back to its base
        // opacity: 1 and stays visible — never stuck invisible.
        opacity: 1,
        animation: 'cookieBannerIn 320ms cubic-bezier(0.22, 1, 0.36, 1) both',
        animationDelay: '120ms',
        '@keyframes cookieBannerIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          transform: 'none',
          animation: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          [BANNER_DESKTOP]: { flexDirection: 'row', alignItems: 'center', gap: '12px' },
        }}
      >
        <Typography
          component="p"
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,
            fontFamily: FONT_BODY,
            fontSize: '12.5px',
            lineHeight: 1.45,
            color: BANNER_TEXT,
            [BANNER_DESKTOP]: { fontSize: '13px' },
          }}
        >
          {texts.banner.description}{' '}
          {/* "Settings" reads as an inline link but stays a <button>: it opens
              the preferences dialog, it does not navigate. */}
          <Box
            component="button"
            type="button"
            onClick={handleOpenSettings}
            sx={{
              display: 'inline',
              p: 0,
              m: 0,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 600,
              color: BANNER_LINK,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              textDecorationThickness: '1px',
              borderRadius: '3px',
              '&:hover': { textDecorationThickness: '2px' },
            }}
          >
            {texts.banner.managePreferences}
          </Box>
        </Typography>

        {/* Same size, same style family - only the fill differs. Phone: the
            two share the row (flex:1 each); desktop: fixed min-width. */}
        <Box sx={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <Button
            variant="text"
            onClick={handleRejectAll}
            disableElevation
            sx={bannerActionSx('#fff', BANNER_ESSENTIAL_BG_HOVER)}
          >
            {texts.banner.rejectNonEssential}
          </Button>
          <Button
            variant="text"
            onClick={handleAcceptAll}
            disableElevation
            sx={bannerActionSx(BANNER_ACCEPT_BG, BANNER_ACCEPT_BG_HOVER)}
          >
            {texts.banner.acceptAll}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  // ---------------------------------------------------------------------------
  // Category row — tinted icon chip + title + description, trailing control
  // (a BrandSwitch, or an "always active" lock pill for necessary cookies).
  // Mirrors SimpleAccessibilitySettings's SettingRow so both panels read as
  // one design system.
  // ---------------------------------------------------------------------------
  const renderCategoryRow = (categoryKey, category, isLast) => {
    const isNecessary = categoryKey === 'necessary';
    const enabled = preferences[categoryKey];
    const Icon = CATEGORY_ICONS[categoryKey] || ShieldCheck;

    return (
      <Box
        key={categoryKey}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.75,
          py: 2,
          borderBottom: isLast ? 'none' : `1px solid ${colors.primary.alpha08}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6, minWidth: 0 }}>
          <RowIcon Icon={Icon} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="p"
              sx={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: '15px', color: '#1A1A2E', mb: 0.25 }}
            >
              {category.title}
            </Typography>
            <Typography
              component="p"
              sx={{ fontFamily: FONT_BODY, fontSize: '13px', color: '#5A6A7E', lineHeight: 1.5 }}
            >
              {category.description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          {isNecessary ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.1,
                py: 0.4,
                borderRadius: '999px',
                backgroundColor: colors.primary.alpha10,
                color: colors.primary.accessible,
                whiteSpace: 'nowrap',
              }}
            >
              <Lock size={11} strokeWidth={2.5} aria-hidden="true" />
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', fontWeight: 700 }}>
                {texts.status.alwaysEnabled}
              </Typography>
            </Box>
          ) : (
            <BrandSwitch
              checked={enabled}
              onChange={() => handleTogglePreference(categoryKey)}
              inputProps={{ 'aria-label': category.title }}
            />
          )}
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Settings dialog — hero header band + scrollable category rows + sticky
  // footer actions. Bottom-sheet on mobile, floating panel on desktop — same
  // structural pattern as SimpleAccessibilitySettings.js.
  //
  // Rendered as a plain function (see renderBanner) so toggling a switch
  // re-renders in place instead of remounting the dialog.
  // ---------------------------------------------------------------------------
  const renderSettings = () => (
    <>
      {/*
        Focus-ring override, scoped by ID (not a class) so it out-specifies
        simple-accessibility.css's global rule:
          *:focus-visible:not(...) { outline: 2px solid #0066CC !important; }
        That rule's chained :not() clauses give it higher specificity than
        any class-based override even with !important on both sides - only an
        ID selector reliably wins. This keeps the sitewide fallback untouched
        everywhere else while making this panel's rings the brand-accessible
        teal (colors.primary.accessible), matching the a11y settings panel.
      */}
      <GlobalStyles
        styles={{
          [`#${PANEL_ID} button:focus-visible, #${PANEL_ID} a:focus-visible, #${PANEL_ID} [role="button"]:focus-visible`]: {
            outline: `2px solid ${colors.primary.accessible} !important`,
            outlineOffset: '2px !important',
          },
        }}
      />
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        aria-labelledby="cookie-settings-title"
        aria-describedby="cookie-settings-description"
        sx={{
          '& .MuiDialog-container': {
            alignItems: { xs: 'flex-end', sm: 'center' },
          },
        }}
        PaperProps={{
          dir,
          id: PANEL_ID,
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 480 },
            maxHeight: { xs: '90vh', sm: '88vh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: '26px 26px 0 0', sm: '22px' },
            background: 'rgba(255,255,255,0.98)',
            border: `1px solid ${colors.primary.alpha18}`,
            boxShadow: '0 24px 70px -12px rgba(20,30,45,0.35), 0 4px 16px rgba(20,30,45,0.10)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Bottom-sheet grabber - decorative affordance, mobile only */}
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'center',
            pt: 1.25,
            pb: 0.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 40, height: 4, borderRadius: 999, background: colors.neutral[300] }} />
        </Box>

        {/* Hero header band */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2.5, sm: 2.75 },
            py: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(150deg, #EDFAF8 0%, #F8FDFF 45%, #FDF3F7 100%)',
            borderBottom: `1px solid ${colors.primary.alpha18}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                flexShrink: 0,
                background: colors.primary.alpha10,
                border: `1px solid ${colors.primary.alpha20}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CookieIcon size={22} color={ICON_COLOR} strokeWidth={1.8} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                id="cookie-settings-title"
                component="h2"
                sx={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: '19px', color: '#1A1A2E', lineHeight: 1.25 }}
              >
                {texts.title}
              </Typography>
            </Box>
          </Box>
          <IconButton
            ref={firstFocusRef}
            onClick={() => setShowSettings(false)}
            aria-label={texts.buttons.close}
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              flexShrink: 0,
              color: '#4A5568',
              background: 'rgba(255,255,255,0.6)',
              '&:hover': { background: '#fff', color: '#1A1A2E' },
            }}
          >
            <CloseIcon size={18} strokeWidth={2} aria-hidden="true" />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 2.5, sm: 2.75 }, pt: 2, pb: { xs: 2.5, sm: 2.25 } }}>
          <Typography
            id="cookie-settings-description"
            sx={{ fontFamily: FONT_BODY, fontSize: '13.5px', lineHeight: 1.6, color: '#3A4A5E', mb: 2.25 }}
          >
            {texts.description}
          </Typography>

          {Object.entries(texts.categories).map(([key, category], index, arr) =>
            renderCategoryRow(key, category, index === arr.length - 1)
          )}

          <Typography sx={{ mt: 2 }}>
            <Link
              href="/privacy"
              sx={{
                fontFamily: FONT_BODY,
                fontSize: '12.5px',
                fontWeight: 700,
                color: colors.primary.accessible,
                textDecoration: 'none',
                borderBottom: '1px solid transparent',
                transition: 'border-color 160ms ease',
                '&:hover': { borderBottomColor: colors.primary.accessible },
              }}
            >
              {texts.privacyPolicy}
            </Link>
          </Typography>
        </Box>

        {/* Sticky footer actions — stacked on mobile, row on desktop.
            Hierarchy unchanged from before the redesign: primary "save" is
            the strongest affordance, "accept all" secondary, "reject all"
            the quietest (text-only) — same order as the previous version. */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row-reverse' },
            alignItems: 'stretch',
            gap: 1,
            px: { xs: 2.5, sm: 2.75 },
            py: { xs: 2, sm: 1.75 },
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderTop: `1px solid ${colors.primary.alpha18}`,
          }}
        >
          <Button
            onClick={handleSavePreferences}
            disableElevation
            sx={{
              flex: { sm: 1 },
              minHeight: 44,
              borderRadius: '999px',
              background: `linear-gradient(135deg, ${colors.primary.accessible}, ${colors.primary.accessibleDark})`,
              color: '#fff',
              fontFamily: FONT_BODY,
              fontWeight: 700,
              fontSize: '13.5px',
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(0,201,176,0.30)',
              '&:hover': { boxShadow: '0 6px 18px rgba(0,201,176,0.42)' },
            }}
          >
            {texts.buttons.acceptSelected}
          </Button>
          <Button
            onClick={handleAcceptAll}
            variant="outlined"
            sx={{
              flex: { sm: 1 },
              minHeight: 44,
              borderRadius: '999px',
              borderColor: colors.primary.alpha28,
              color: colors.primary.accessible,
              fontFamily: FONT_BODY,
              fontWeight: 700,
              fontSize: '13.5px',
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.primary.accessible,
                backgroundColor: colors.primary.alpha06,
              },
            }}
          >
            {texts.buttons.acceptAll}
          </Button>
          <Button
            onClick={handleRejectAll}
            sx={{
              flex: { sm: '0 0 auto' },
              minHeight: 44,
              borderRadius: '999px',
              color: '#5A6A7E',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '13.5px',
              textTransform: 'none',
              '&:hover': { backgroundColor: colors.neutral[100], color: '#1A1A2E' },
            }}
          >
            {texts.buttons.rejectAll}
          </Button>
        </Box>
      </Dialog>
    </>
  );

  return (
    <>
      {showBanner && !showSettings && renderBanner()}
      {showSettings && renderSettings()}

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%', borderRadius: '8px' }}
        >
          {texts.messages.saved}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CookieConsentBanner;
